import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StoreCacheContext } from 'src/contexts/StoreCacheContext';
import { Filter, GroupLevel } from 'src/domain';
import { useStoreType } from 'src/hooks/useStoreType';
import { useType } from 'src/hooks/useType';
import { StoreModel, StoreType } from 'src/infrastructure/rest-api/api-types';
import { GroupedStoresResult } from 'src/mapping/store.mapping';

export const useStores = (groupBy?: GroupLevel | null, storeType?: StoreType, filter?: Filter) => {
    const [getStores, { error }] = useContext(StoreCacheContext);
    const [loading, setLoading] = useState(true);

    const defaultGroupBy = useType();
    const defaultStoreType = useStoreType();
    const filterKey = useMemo(() => JSON.stringify(filter ?? null), [filter]);
    const params = useMemo(
        () => ({
            groupBy: groupBy ?? defaultGroupBy,
            storeType: storeType ?? defaultStoreType,
            filter,
        }),
        [groupBy, defaultGroupBy, storeType, defaultStoreType, filterKey]
    );

    const [stores, setStores] = useState<StoreModel[]>([]);
    const [data, setData] = useState<GroupedStoresResult>(() => ({
        groupedStores: undefined,
        storeCount: 0,
        chains: [],
        clusters: [],
        countries: [],
        partners: [],
        allStores: [],
        allStoresLookup: new Map(),
    }));

    const fetchStores = useCallback(async () => {
        const result = getStores(params.groupBy, params.storeType, params.filter);
        setData(result.groupedStores);
        setStores(result.allStores ?? []);
        setLoading(result.loading ?? false);
    }, [params, getStores]);

    useEffect(() => {
        if (!params.storeType) return;
        fetchStores();
    }, [fetchStores, params.storeType]);

    return {
        stores,
        data,
        loading,
        error,
    };
};
